import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton, useAuth, useUser } from '@clerk/clerk-react';
import { Button } from '../components/ui/button';

const CONVEX_URL = process.env.REACT_APP_CONVEX_URL;
const rawConvexHttpUrl =
  process.env.REACT_APP_CONVEX_HTTP_URL ||
  (CONVEX_URL ? CONVEX_URL.replace('.convex.cloud', '.convex.site') : '');
const CONVEX_HTTP_URL = (() => {
  if (!rawConvexHttpUrl) {
    return '';
  }
  try {
    // Normalize to origin only in case a path was included in env vars.
    return new URL(rawConvexHttpUrl).origin;
  } catch (error) {
    console.error('Invalid Convex HTTP URL:', error);
    return '';
  }
})();

const AlexaOAuthPage = () => {
  const [searchParams] = useSearchParams();
  const { sessionId } = useAuth();
  const { user } = useUser();
  const [error, setError] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  const clientId = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');
  const state = searchParams.get('state');
  const responseType = searchParams.get('response_type');

  useEffect(() => {
    if (!clientId || !redirectUri) {
      setError('Missing required parameters. Please restart account linking.');
      return;
    }

    if (responseType && responseType !== 'code') {
      setError('Invalid response type.');
    }
  }, [clientId, redirectUri, responseType]);

  useEffect(() => {
    const issueCode = async () => {
      if (!CONVEX_HTTP_URL) {
        setError('Missing Convex configuration.');
        return;
      }

      if (!clientId || !redirectUri || !sessionId || hasStarted || isLinking || error) {
        return;
      }

      setHasStarted(true);
      setIsLinking(true);

      try {
        const response = await fetch(`${CONVEX_HTTP_URL}/alexa/oauth/issue-code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            clientId,
            redirectUri,
            state,
          }),
        });

        if (!response.ok) {
          let message = 'Failed to generate authorization code.';
          const contentType = response.headers.get('content-type') || '';
          try {
            if (contentType.includes('application/json')) {
              const body = await response.json();
              message = body?.error || body?.message || message;
            } else {
              const text = await response.text();
              if (text) {
                message = text;
              }
            }
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }
          throw new Error(message);
        }

        const data = await response.json();
        if (!data.code) {
          throw new Error('No authorization code returned.');
        }

        const url = new URL(redirectUri);
        url.searchParams.set('code', data.code);
        if (state) {
          url.searchParams.set('state', state);
        }

        window.location.assign(url.toString());
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : 'We could not link your account. Please try again.';
        console.error('Alexa OAuth linking error:', err);
        setError(message);
      } finally {
        setIsLinking(false);
      }
    };

    issueCode();
  }, [clientId, redirectUri, sessionId, state, hasStarted, isLinking, error]);

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white">
      <section className="pt-10 pb-10 px-4">
        <div className="max-w-lg mx-auto text-center">
          <h1 className="text-3xl font-bold text-saverly-dark font-poppins mb-3">
            Link Your Saverly Account
          </h1>
          <p className="text-gray-600 font-inter">
            Sign in to connect Alexa with your Saverly shopping list.
          </p>
        </div>
      </section>

      <section className="pb-12 px-4">
        <div className="max-w-md mx-auto bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center">
          {error && (
            <div className="mb-6 text-sm text-red-600">
              {error}
            </div>
          )}

          <SignedOut>
            <SignInButton mode="modal">
              <Button className="w-full bg-saverly-primary hover:bg-saverly-deep text-white px-6 py-3 rounded-xl font-semibold shadow-sm transition-all">
                Sign In to Link Alexa
              </Button>
            </SignInButton>
          </SignedOut>

          <SignedIn>
            <div className="text-gray-700 font-inter">
              {isLinking
                ? 'Linking your account...'
                : `Signed in as ${user?.primaryEmailAddress?.emailAddress || 'your account'}.`}
            </div>
          </SignedIn>
        </div>
      </section>
    </div>
  );
};

export default AlexaOAuthPage;
